import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import http from "../services/http";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../contexts/SnackbarContext";
import AdminLayout from "../components/AdminLayout";

const ArtistCreate: React.FC = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const { notify } = useSnackbar();

  const [name, setName] = useState("");
  const [regionCode, setRegionCode] = useState("");
  const [bio, setBio] = useState("");
  const [avatar, setAvatar] = useState<File | null>(null);

  const [regions, setRegions] = useState<{ code: string; label: string }[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fieldError, setFieldError] = useState<string | null>(null);

  // 加载地区选项
  useEffect(() => {
    const loadRegions = async () => {
      if (!token) return;
      setRegionsLoading(true);
      try {
        const res = await http.get(`region`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.code === "000000") {
          const items = (res.data.data?.regions ?? []).map((r: any) => ({
            code: r.regionCode,
            label: r.regionNameZh,
          }));
          setRegions(items);
          setRegionCode((prev) => (!prev && items.length > 0 ? items[0].code : prev));
        } else {
          notify(res.data?.message || "地区加载失败", "error");
        }
      } catch (err: any) {
        console.error("加载地区失败:", err);
        if (err.response?.status === 401) {
          notify("登录已过期，请重新登录", "error");
          logout();
          navigate("/");
        } else {
          notify("网络错误，地区加载失败", "error");
        }
      } finally {
        setRegionsLoading(false);
      }
    };
    loadRegions();
    // 仅在初次加载与 token 变化时拉取
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, logout, navigate, notify]);

  useEffect(() => {
    if (!avatar) return setAvatarPreview(null);
    const url = URL.createObjectURL(avatar);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatar]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setAvatar(file);
  };

  const handleSubmit = async () => {
    if (!name || !regionCode) {
      setFieldError("请填写名字和国家/地区");
      return;
    }
    setFieldError(null);
    if (!token) {
      notify("请先登录", "error");
      navigate("/");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("regionCode", regionCode);
    formData.append("bio", bio);
    if (avatar) formData.append("avatar", avatar);

    try {
      setSubmitting(true);
      setProgress(0);
      const res = await http.post(
        `artist`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
          },
        }
      );

      if (res.data.code === "000000") {
        notify("新增成功", "success");
        navigate("/artist");
      } else {
        notify(res.data.message || "新增失败", "error");
      }
    } catch (err: any) {
      console.error(err);
      if (err.response?.status === 401) {
        notify("登录已过期，请重新登录", "error");
        logout();
        navigate("/");
      } else {
        const backendMsg = err.response?.data?.message || err.message;
        notify(`新增失败：${backendMsg}`, "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout title="新增艺术家">
      <Box sx={{ maxWidth: 640 }}>
        <Stack spacing={2}>
          <TextField
            label="名字"
            required
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!fieldError && !name}
          />

          <TextField
            select
            label="国家/地区"
            required
            fullWidth
            value={regionCode}
            onChange={(e) => setRegionCode(e.target.value)}
            error={!!fieldError && !regionCode}
            helperText={fieldError}
          >
            {regionsLoading ? (
              <option value="" disabled>
                加载中...
              </option>
            ) : (
              regions.map((r) => (
                <option key={r.code} value={r.code}>{`${r.label} (${r.code})`}</option>
              ))
            )}
          </TextField>

          <TextField
            label="介绍"
            multiline
            minRows={3}
            fullWidth
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />

          <Box>
            <Button variant="outlined" component="label" fullWidth>
              头像图片
              <input type="file" hidden accept="image/*" onChange={handleFileChange} />
            </Button>
            {avatarPreview && (
              <Card sx={{ mt: 1 }}>
                <CardContent>
                  <img
                    src={avatarPreview}
                    alt="avatar"
                    style={{ width: "100%", maxHeight: 240, objectFit: "cover" }}
                  />
                </CardContent>
              </Card>
            )}
          </Box>

          {submitting && (
            <Box>
              <Typography variant="body2" gutterBottom>
                正在提交...
              </Typography>
              <LinearProgress value={progress} variant={progress ? "determinate" : "indeterminate"} />
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={submitting || !name || !regionCode}
          >
            {submitting ? "提交中..." : "提交"}
          </Button>
        </Stack>
      </Box>
    </AdminLayout>
  );
};

export default ArtistCreate;
